import { useState } from 'react'
import { FormInput, FormSelect } from '../../shared/components/Fieldset'
import Pagination from '../../shared/components/Pagination'
import { FaChevronDown, FaChevronUp } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import { languageUtil } from '../../util/languageUtils'

const groupByCategory = items => {
    const { language } = languageUtil()
    const rtl = language === 'ar'

    return items.reduce((groups, item) => {
        const category = item.project_category_name || 'Uncategorized'
        if (!groups[category]) groups[category] = []
        groups[category].push(item)
        return groups
    }, {})
}

const CardCategory = ({ data, renderCard, itemsPerPage = 30, filters = [] }) => {
    const [currentPage, setCurrentPage] = useState(1)
    const [searchQuery, setSearchQuery] = useState('')
    const [activeFilters, setActiveFilters] = useState({})
    const { t } = useTranslation()

    const handleFilterChange = (name, value) => {
        setActiveFilters(prev => ({
            ...prev,
            [name]: value,
        }))
        setCurrentPage(1)

        const filter = filters.find(f => f.name === name)
        if (filter?.onChange) {
            filter.onChange(value)
        }
    }

    const filteredData = data.filter(item => {
        const matchesSearch = item.title?.toLowerCase().includes(searchQuery.toLowerCase())

        return matchesSearch
    })

    const grouped = groupByCategory(filteredData)

    const categories = Object.keys(grouped)

    // Optional pagination per category (can remove if not needed)
    const totalPages = Math.ceil(filteredData.length / itemsPerPage)
    const handlePageChange = page => {
        if (page >= 1 && page <= totalPages) setCurrentPage(page)
    }

    return (
        <div className='px-4 space-y-8 sm:px-6 lg:px-8'>
            {/* Filters */}
            <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'>
                {filters.map((filter, index) => (
                    <FormSelect
                        label={filter.label}
                        key={index}
                        placeholder={filter.placeholder}
                        value={activeFilters[filter.name] || ''}
                        options={filter.options}
                        onChange={e => handleFilterChange(filter.name, e.target.value)}
                    />
                ))}
                <div></div>
                <div></div>
                <FormInput
                    className='mt-6'
                    placeholder={t('search.title')}
                    value={searchQuery}
                    onChange={e => {
                        setSearchQuery(e.target.value)
                        setCurrentPage(1)
                    }}
                />
            </div>

            {/* Grouped by category */}
            <GroupedCardGrid grouped={grouped} renderCard={renderCard} />

            {/* Optional pagination (for the entire data set) */}
            <div className='flex justify-center'>
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                />
            </div>
        </div>
    )
}

const GroupedCardGrid = ({ grouped, renderCard }) => {
    const categories = Object.keys(grouped)
    const [openCategories, setOpenCategories] = useState(
        categories.reduce((acc, cat) => {
            acc[cat] = true // default: all open
            return acc
        }, {})
    )

    const toggleCategory = category => {
        setOpenCategories(prev => ({
            ...prev,
            [category]: !prev[category],
        }))
    }

    return (
        <div className='space-y-8'>
            {categories.map((category, idx) => (
                <div key={idx} className='space-y-4'>
                    <div
                        className='flex items-center justify-between gap-4 text-xl cursor-pointer'
                        onClick={() => toggleCategory(category)}
                    >
                        <h2 className='text-xl font-semibold text-gray-800 highlighter'>
                            {category}
                        </h2>
                        <button>
                            {openCategories[category] ? <FaChevronDown /> : <FaChevronUp />}
                        </button>
                    </div>
                    {openCategories[category] && (
                        <div className='grid grid-cols-1 gap-6 pb-8 sm:grid-cols-2 lg:grid-cols-3'>
                            {grouped[category].map((item, index) => (
                                <div key={index} className='w-full'>
                                    {renderCard(item)}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    )
}

export default CardCategory
